import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Snackbar,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  LinearProgress,
  TableSortLabel,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Print,
  Person,
  AttachMoney,
  TrendingDown,
  TrendingUp,
  AccountBalance,
  Hotel,
  Restaurant,
  FitnessCenter,
  Receipt,
  Download,
  PictureAsPdf,
  BarChart,
  PieChart as PieChartIcon,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  Assessment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart,
  Line,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Hotel/api';
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'e-Neurons Luxury Hotel';
const HOTEL_ADDRESS = process.env.REACT_APP_HOTEL_ADDRESS || 'Luxury Hotel Address';
const HOTEL_PHONE = process.env.REACT_APP_HOTEL_PHONE;

const FinancialLedger = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const chartRef = useRef();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
    net_profit: 0,
    profit_margin: 0,
    income_by_source: {},
    expense_by_category: {},
    daily_summary: [],
    total_transactions: 0,
    income_transactions: 0,
    expense_transactions: 0
  });
  const [breakdown, setBreakdown] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentStaff, setCurrentStaff] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [viewMode, setViewMode] = useState('summary'); // summary, details, charts
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    transactionType: 'all', // all, income, expense
    source: '',
    dateRange: 'today',
  });
  
  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Sorting
  const [orderBy, setOrderBy] = useState('transaction_date');
  const [order, setOrder] = useState('desc');
  
  // Navigation and sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Color palette for charts
  const COLORS = {
    room_booking: '#4caf50',
    fb_order: '#ff9800',
    facility_booking: '#2196f3',
    other_income: '#9c27b0',
    expense: '#f44336',
  };
  
  const PIE_COLORS = ['#4caf50', '#ff9800', '#2196f3', '#9c27b0', '#f44336', '#00bcd4', '#e91e63'];

  // Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // Get hotel code and staff details
  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || 'eHT5678042024';
    } catch (error) {
      return 'eHT5678042024';
    }
  };

  const getStaffName = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return `${storedProfile.first_name || ''} ${storedProfile.last_name || ''}`.trim() || 'Accountant';
    } catch (error) {
      return 'Accountant';
    }
  };

  const getCurrentStaff = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      setCurrentStaff(storedProfile);
      return storedProfile;
    } catch (error) {
      return null;
    }
  };

  // Helper function to extract date
  const extractDate = (datetime) => {
    if (!datetime) return '';
    if (datetime.includes(' ')) return datetime.split(' ')[0];
    if (datetime.includes('T')) return datetime.split('T')[0];
    return datetime;
  };

  // API service functions
  const api = {
    getLedgerEntries: async (params = {}) => {
      try {
        const queryParams = { 
          hotel_code: getHotelCode(), 
          start_date: params.start_date,
          end_date: params.end_date,
        };
        
        console.log('Calling ledger API with params:', queryParams);
        
        const response = await axios.get(`${API_BASE_URL}/hotel/account/ledger.php`, {
          params: queryParams,
          timeout: 15000,
        });
        
        console.log('Ledger API response:', response.data);
        
        if (response.data && response.data.success) {
          return { 
            success: true, 
            entries: response.data.entries || [],
            summary: response.data.summary || {}
          };
        }
        return { success: false, entries: [], summary: {}, error: response.data?.error };
      } catch (error) {
        console.error('Error fetching ledger entries:', error);
        return { success: false, entries: [], summary: {}, error: error.message };
      }
    },

    getBreakdown: async (params = {}) => {
      try {
        const queryParams = { 
          hotel_code: getHotelCode(), 
          start_date: params.start_date,
          end_date: params.end_date,
          breakdown: 'true'
        };
        
        const response = await axios.get(`${API_BASE_URL}/hotel/account/ledger.php`, {
          params: queryParams,
          timeout: 10000,
        });
        
        if (response.data && response.data.success) {
          return { success: true, breakdown: response.data.breakdown || {} };
        }
        return { success: false, breakdown: {} };
      } catch (error) {
        console.error('Error fetching breakdown:', error);
        return { success: false, breakdown: {} };
      }
    }
  };

  // Load data function
  const loadData = async () => {
    setLoading(true);
    try {
      const startDate = filters.startDate;
      const endDate = filters.endDate;
      
      console.log('Loading ledger data with dates:', { startDate, endDate });
      
      const [ledgerData, breakdownData] = await Promise.all([
        api.getLedgerEntries({ start_date: startDate, end_date: endDate }),
        api.getBreakdown({ start_date: startDate, end_date: endDate })
      ]);

      if (ledgerData.success) {
        setEntries(ledgerData.entries);
        setFilteredEntries(ledgerData.entries);
        if (ledgerData.summary) {
          setSummary(ledgerData.summary);
        }
      } else {
        showSnackbar(ledgerData.error || 'Failed to load ledger data', 'error');
      }

      if (breakdownData.success) {
        setBreakdown(breakdownData.breakdown);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    if (!entries || entries.length === 0) {
      setFilteredEntries([]);
      return;
    }
    
    let filtered = [...entries];

    if (filters.transactionType !== 'all') {
      filtered = filtered.filter(entry => entry.type === filters.transactionType);
    }

    if (filters.source) {
      filtered = filtered.filter(entry => entry.source === filters.source);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.description?.toLowerCase().includes(query) ||
        entry.reference_number?.toLowerCase().includes(query) ||
        entry.category?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (orderBy === 'transaction_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredEntries(filtered);
    setPage(0);
  };

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    const today = new Date();
    
    switch (range) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'today'
        }));
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFilters(prev => ({
          ...prev,
          startDate: yesterday.toISOString().split('T')[0],
          endDate: yesterday.toISOString().split('T')[0],
          dateRange: 'yesterday'
        }));
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          startDate: startOfWeek.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisWeek'
        }));
        break;
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisMonth'
        }));
        break;
      case 'lastMonth':
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setFilters(prev => ({
          ...prev,
          startDate: firstDayLastMonth.toISOString().split('T')[0],
          endDate: lastDayLastMonth.toISOString().split('T')[0],
          dateRange: 'lastMonth'
        }));
        break;
      case 'custom':
        setFilters(prev => ({ ...prev, dateRange: 'custom' }));
        break;
      default:
        break;
    }
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleDateChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
      dateRange: 'custom'
    }));
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      startDate: today,
      endDate: today,
      transactionType: 'all',
      source: '',
      dateRange: 'today',
    });
    setSearchQuery('');
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry);
    setDetailsDialogOpen(true);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSourceLabel = (source) => {
    const labels = {
      room_booking: 'Room Booking',
      fb_order: 'Food & Beverage',
      facility_booking: 'Facility Booking',
      other_income: 'Other Income',
      expense: 'Expense'
    };
    return labels[source] || source;
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'room_booking': return <Hotel sx={{ fontSize: 20 }} />;
      case 'fb_order': return <Restaurant sx={{ fontSize: 20 }} />;
      case 'facility_booking': return <FitnessCenter sx={{ fontSize: 20 }} />;
      case 'other_income': return <AttachMoney sx={{ fontSize: 20 }} />;
      case 'expense': return <TrendingDown sx={{ fontSize: 20 }} />;
      default: return <Receipt sx={{ fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getDateRangeText = () => {
    switch (filters.dateRange) {
      case 'today': return "Today's Financial Report";
      case 'yesterday': return "Yesterday's Financial Report";
      case 'thisWeek': return "This Week's Financial Report";
      case 'thisMonth': return "This Month's Financial Report";
      case 'lastMonth': return "Last Month's Financial Report";
      case 'custom': return `Financial Report from ${filters.startDate} to ${filters.endDate}`;
      default: return "Financial Report";
    }
  };

  // Prepare data for charts
  const prepareDailyChartData = () => {
    if (!summary.daily_summary || summary.daily_summary.length === 0) {
      return [];
    }
    return summary.daily_summary;
  };

  const preparePieChartData = () => {
    const incomeSources = summary.income_by_source || {};
    const data = Object.entries(incomeSources).map(([source, amount]) => ({
      name: getSourceLabel(source),
      value: amount,
      source: source
    }));
    return data;
  };

  const prepareExpensePieData = () => {
    const expenseCategories = summary.expense_by_category || {};
    const data = Object.entries(expenseCategories).map(([category, amount]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      value: amount,
      category: category
    }));
    return data;
  };

  // PDF Generation
  const generatePDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(0, 100, 0);
    doc.text(HOTEL_NAME, pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(HOTEL_ADDRESS, pageWidth / 2, 30, { align: 'center' });
    doc.text(`Tel: ${HOTEL_PHONE || 'N/A'}`, pageWidth / 2, 37, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(getDateRangeText(), pageWidth / 2, 47, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated by: ${getStaffName()} | Date: ${new Date().toLocaleString()}`, pageWidth / 2, 55, { align: 'center' });
    
    doc.line(20, 60, pageWidth - 20, 60);
    
    // Summary Section
    doc.setFontSize(12);
    doc.setTextColor(0, 100, 0);
    doc.text('FINANCIAL SUMMARY', 20, 70);
    
    doc.setFontSize(10);
    doc.setTextColor(0);
    
    const summaryY = 80;
    doc.text(`Total Income: ₦${summary.total_income?.toLocaleString() || 0}`, 20, summaryY);
    doc.text(`Total Expenses: ₦${summary.total_expense?.toLocaleString() || 0}`, 80, summaryY);
    doc.text(`Net Profit: ₦${summary.net_profit?.toLocaleString() || 0}`, 140, summaryY);
    doc.text(`Profit Margin: ${summary.profit_margin?.toFixed(2) || 0}%`, 200, summaryY);
    
    // Transactions Table
    const tableY = 95;
    doc.text('TRANSACTION DETAILS', 20, tableY);
    
    const tableData = filteredEntries.slice(0, 50).map(entry => [
      entry.reference_number || '-',
      entry.transaction_date,
      getSourceLabel(entry.source),
      entry.category || '-',
      entry.type === 'income' ? `₦${entry.amount?.toLocaleString()}` : '',
      entry.type === 'expense' ? `₦${entry.amount?.toLocaleString()}` : '',
      entry.payment_status || entry.status,
    ]);
    
    autoTable(doc, {
      startY: tableY + 5,
      head: [['Ref #', 'Date', 'Source', 'Category', 'Income', 'Expense', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [76, 175, 80], textColor: [255, 255, 255] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 25 },
        2: { cellWidth: 35 },
        3: { cellWidth: 40 },
        4: { cellWidth: 35, halign: 'right' },
        5: { cellWidth: 35, halign: 'right' },
        6: { cellWidth: 30 },
      },
    });
    
    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(`Total Transactions: ${filteredEntries.length} | Income: ${summary.income_transactions || 0} | Expense: ${summary.expense_transactions || 0}`, 20, finalY);
    doc.text('This is a computer-generated document. No signature required.', pageWidth / 2, finalY + 10, { align: 'center' });
    
    doc.save(`financial_ledger_${filters.startDate}_to_${filters.endDate}.pdf`);
  };

  // Excel Export
  const exportToExcel = () => {
    const exportData = filteredEntries.map(entry => ({
      'Reference Number': entry.reference_number,
      'Transaction Date': entry.transaction_date,
      'Type': entry.type === 'income' ? 'Income' : 'Expense',
      'Source': getSourceLabel(entry.source),
      'Category': entry.category,
      'Amount (₦)': entry.amount,
      'Status': entry.payment_status || entry.status,
      'Description': entry.description,
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financial Ledger');
    XLSX.writeFile(wb, `financial_ledger_${filters.startDate}_to_${filters.endDate}.xlsx`);
    
    showSnackbar('Exported to Excel successfully!', 'success');
  };

  // Load initial data
  useEffect(() => {
    getCurrentStaff();
    loadData();
  }, []);

  // Reload data when date range changes
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadData();
    }
  }, [filters.startDate, filters.endDate]);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [entries, filters.transactionType, filters.source, searchQuery, orderBy, order]);

  // Handle date range changes
  useEffect(() => {
    if (filters.dateRange && filters.dateRange !== 'custom') {
      handleDateRangeChange(filters.dateRange);
    }
  }, [filters.dateRange]);

  const dailyChartData = prepareDailyChartData();
  const pieChartData = preparePieChartData();
  const expensePieData = prepareExpensePieData();

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  <AccountBalance sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
                  Financial Ledger
                </Typography>
                <Typography variant="subtitle1">{getDateRangeText()}</Typography>
                {currentStaff && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Accountant: {currentStaff.first_name} {currentStaff.last_name || ''} ({currentStaff.user_type})
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" startIcon={<PictureAsPdf />} onClick={generatePDF} sx={{ backgroundColor: 'white', color: 'success.main' }}>
                  Export PDF
                </Button>
                <Button variant="contained" startIcon={<Download />} onClick={exportToExcel} sx={{ backgroundColor: 'white', color: 'info.main' }}>
                  Export Excel
                </Button>
                <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterDialogOpen(true)} sx={{ borderColor: 'white', color: 'white' }}>
                  Filters
                </Button>
                <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} disabled={loading} sx={{ borderColor: 'white', color: 'white' }}>
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* View Mode Toggle */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              aria-label="view mode"
              size="small"
            >
              <ToggleButton value="summary" aria-label="summary">
                <Assessment sx={{ mr: 1 }} /> Summary
              </ToggleButton>
              <ToggleButton value="charts" aria-label="charts">
                <BarChart sx={{ mr: 1 }} /> Charts
              </ToggleButton>
              <ToggleButton value="details" aria-label="details">
                <Receipt sx={{ mr: 1 }} /> Details
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {/* Summary Cards */}
          {viewMode === 'summary' && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #4caf50, #45a049)', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography gutterBottom variant="overline">Total Income</Typography>
                          <Typography variant="h4">
                            ₦{(summary.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <TrendingUp sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #f44336, #d32f2f)', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography gutterBottom variant="overline">Total Expenses</Typography>
                          <Typography variant="h4">
                            ₦{(summary.total_expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <TrendingDown sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #2196f3, #1976d2)', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography gutterBottom variant="overline">Net Profit</Typography>
                          <Typography variant="h4">
                            ₦{(summary.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <AccountBalance sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #ff9800, #f57c00)', color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography gutterBottom variant="overline">Profit Margin</Typography>
                          <Typography variant="h4">
                            {summary.profit_margin?.toFixed(2) || 0}%
                          </Typography>
                        </Box>
                        <Assessment sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Transaction Stats */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">Total Transactions</Typography>
                    <Typography variant="h3">{summary.total_transactions || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                    <Typography variant="h6" color="success.main">Income Transactions</Typography>
                    <Typography variant="h3">{summary.income_transactions || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                    <Typography variant="h6" color="error.main">Expense Transactions</Typography>
                    <Typography variant="h3">{summary.expense_transactions || 0}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Income by Source Breakdown */}
              {Object.keys(summary.income_by_source || {}).length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Income by Source</Typography>
                  <Grid container spacing={2}>
                    {Object.entries(summary.income_by_source).map(([source, amount]) => (
                      <Grid item xs={12} sm={6} md={4} key={source}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                          {getSourceIcon(source)}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">{getSourceLabel(source)}</Typography>
                            <Typography variant="h6" color="success.main">
                              ₦{amount.toLocaleString()}
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${((amount / summary.total_income) * 100).toFixed(1)}%`} 
                            size="small" 
                            color="success" 
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              {/* Expense by Category Breakdown */}
              {Object.keys(summary.expense_by_category || {}).length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Expense by Category</Typography>
                  <Grid container spacing={2}>
                    {Object.entries(summary.expense_by_category).map(([category, amount]) => (
                      <Grid item xs={12} sm={6} md={4} key={category}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                          <TrendingDown sx={{ color: '#f44336' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">{category.charAt(0).toUpperCase() + category.slice(1)}</Typography>
                            <Typography variant="h6" color="error.main">
                              ₦{amount.toLocaleString()}
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${((amount / summary.total_expense) * 100).toFixed(1)}%`} 
                            size="small" 
                            color="error" 
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
            </>
          )}

          {/* Charts View */}
          {viewMode === 'charts' && (
            <>
              {/* Chart Type Toggle */}
              <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <ToggleButtonGroup
                  value={chartType}
                  exclusive
                  onChange={(e, val) => val && setChartType(val)}
                  aria-label="chart type"
                  size="small"
                >
                  <ToggleButton value="line" aria-label="line chart">
                    <Assessment /> Line Chart
                  </ToggleButton>
                  <ToggleButton value="bar" aria-label="bar chart">
                    <BarChart /> Bar Chart
                  </ToggleButton>
                </ToggleButtonGroup>
              </Paper>

              {/* Daily Performance Chart */}
              {dailyChartData.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Daily Financial Performance</Typography>
                  <Box sx={{ height: 400, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {chartType === 'line' ? (
                        <LineChart data={dailyChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`} />
                          <ReTooltip formatter={(value) => [`₦${value.toLocaleString()}`, '']} />
                          <Legend />
                          <Line type="monotone" dataKey="income" stroke="#4caf50" name="Income" strokeWidth={2} />
                          <Line type="monotone" dataKey="expense" stroke="#f44336" name="Expense" strokeWidth={2} />
                          <Line type="monotone" dataKey="profit" stroke="#2196f3" name="Profit" strokeWidth={2} />
                        </LineChart>
                      ) : (
                        <ReBarChart data={dailyChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`} />
                          <ReTooltip formatter={(value) => [`₦${value.toLocaleString()}`, '']} />
                          <Legend />
                          <Bar dataKey="income" fill="#4caf50" name="Income" />
                          <Bar dataKey="expense" fill="#f44336" name="Expense" />
                        </ReBarChart>
                      )}
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              )}

              {/* Income Distribution Pie Chart */}
              {pieChartData.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Income Distribution by Source</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              outerRadius={120}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <ReTooltip formatter={(value) => [`₦${value.toLocaleString()}`, 'Amount']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mt: 2 }}>
                        {pieChartData.map((item, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: PIE_COLORS[index % PIE_COLORS.length] }} />
                              <Typography variant="body2">{item.name}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                ₦{item.value.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({((item.value / summary.total_income) * 100).toFixed(1)}%)
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" fontWeight="bold">Total Income:</Typography>
                          <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                            ₦{summary.total_income?.toLocaleString() || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Expense Distribution Pie Chart */}
              {expensePieData.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Expense Distribution by Category</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={expensePieData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                              outerRadius={120}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {expensePieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <ReTooltip formatter={(value) => [`₦${value.toLocaleString()}`, 'Amount']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mt: 2 }}>
                        {expensePieData.map((item, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: PIE_COLORS[index % PIE_COLORS.length] }} />
                              <Typography variant="body2">{item.name}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                ₦{item.value.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ({((item.value / summary.total_expense) * 100).toFixed(1)}%)
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" fontWeight="bold">Total Expenses:</Typography>
                          <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                            ₦{summary.total_expense?.toLocaleString() || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </>
          )}

          {/* Details View - Transactions Table */}
          {viewMode === 'details' && (
            <>
              {/* Quick Date Filters */}
              <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Quick Date Filters</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Button variant={filters.dateRange === 'today' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'today' }))} size="small">Today</Button>
                  <Button variant={filters.dateRange === 'yesterday' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'yesterday' }))} size="small">Yesterday</Button>
                  <Button variant={filters.dateRange === 'thisWeek' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'thisWeek' }))} size="small">This Week</Button>
                  <Button variant={filters.dateRange === 'thisMonth' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'thisMonth' }))} size="small">This Month</Button>
                  <Button variant={filters.dateRange === 'lastMonth' ? 'contained' : 'outlined'} onClick={() => setFilters(prev => ({ ...prev, dateRange: 'lastMonth' }))} size="small">Last Month</Button>
                </Stack>
              </Paper>

              {/* Transaction Type Filters */}
              <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Transaction Type</InputLabel>
                      <Select value={filters.transactionType} onChange={handleFilterChange('transactionType')} label="Transaction Type">
                        <MenuItem value="all">All Transactions</MenuItem>
                        <MenuItem value="income">Income Only</MenuItem>
                        <MenuItem value="expense">Expense Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select value={filters.source} onChange={handleFilterChange('source')} label="Source">
                        <MenuItem value="">All Sources</MenuItem>
                        <MenuItem value="room_booking">Room Bookings</MenuItem>
                        <MenuItem value="fb_order">Food & Beverage</MenuItem>
                        <MenuItem value="facility_booking">Facility Bookings</MenuItem>
                        <MenuItem value="other_income">Other Income</MenuItem>
                        <MenuItem value="expense">Expenses</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by reference, description, or category..."
                      value={searchQuery}
                      onChange={handleSearch}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Transactions Table */}
              <Paper elevation={3}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: theme.palette.success.main }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                              <TableSortLabel
                                active={orderBy === 'reference_number'}
                                direction={orderBy === 'reference_number' ? order : 'asc'}
                                onClick={() => handleSort('reference_number')}
                                sx={{ color: 'white', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                              >
                                Reference #
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                              <TableSortLabel
                                active={orderBy === 'transaction_date'}
                                direction={orderBy === 'transaction_date' ? order : 'asc'}
                                onClick={() => handleSort('transaction_date')}
                                sx={{ color: 'white', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                              >
                                Date
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Source</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                              <TableSortLabel
                                active={orderBy === 'amount'}
                                direction={orderBy === 'amount' ? order : 'asc'}
                                onClick={() => handleSort('amount')}
                                sx={{ color: 'white', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                              >
                                Amount
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredEntries.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                                  No transactions found for the selected period
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((entry) => (
                              <TableRow key={`${entry.source}_${entry.id}`} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">{entry.reference_number}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{entry.transaction_date}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    icon={getSourceIcon(entry.source)}
                                    label={getSourceLabel(entry.source)}
                                    size="small"
                                    sx={{ 
                                      bgcolor: entry.type === 'income' ? '#4caf50' : '#f44336',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{entry.category || '-'}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="bold" 
                                    color={entry.type === 'income' ? 'success.main' : 'error.main'}
                                  >
                                    {entry.type === 'income' ? '+' : '-'} ₦{entry.amount?.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={entry.type === 'income' ? 'Income' : 'Expense'} 
                                    size="small"
                                    color={entry.type === 'income' ? 'success' : 'error'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={entry.payment_status || entry.status || 'completed'} 
                                    size="small" 
                                    color={getStatusColor(entry.payment_status || entry.status)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="View Details">
                                    <IconButton size="small" color="primary" onClick={() => handleViewDetails(entry)}>
                                      <Visibility />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {filteredEntries.length > 0 && (
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredEntries.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                      />
                    )}
                  </>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="success" />
            <Typography variant="h6">Filter Financial Report</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Start Date" 
                type="date" 
                value={filters.startDate} 
                onChange={handleDateChange('startDate')} 
                InputLabelProps={{ shrink: true }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="End Date" 
                type="date" 
                value={filters.endDate} 
                onChange={handleDateChange('endDate')} 
                InputLabelProps={{ shrink: true }} 
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select value={filters.transactionType} onChange={handleFilterChange('transactionType')} label="Transaction Type">
                  <MenuItem value="all">All Transactions</MenuItem>
                  <MenuItem value="income">Income Only</MenuItem>
                  <MenuItem value="expense">Expense Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select value={filters.source} onChange={handleFilterChange('source')} label="Source">
                  <MenuItem value="">All Sources</MenuItem>
                  <MenuItem value="room_booking">Room Bookings</MenuItem>
                  <MenuItem value="fb_order">Food & Beverage Orders</MenuItem>
                  <MenuItem value="facility_booking">Facility Bookings</MenuItem>
                  <MenuItem value="other_income">Other Incomes</MenuItem>
                  <MenuItem value="expense">Expenses</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => setFilterDialogOpen(false)}>Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Transaction Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEntry && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Reference Information</Typography>
                <Typography variant="body1"><strong>Reference #:</strong> {selectedEntry.reference_number}</Typography>
                <Typography variant="body1"><strong>Transaction Date:</strong> {selectedEntry.transaction_date}</Typography>
                <Typography variant="body1"><strong>Type:</strong> 
                  <Chip label={selectedEntry.type === 'income' ? 'Income' : 'Expense'} size="small" color={selectedEntry.type === 'income' ? 'success' : 'error'} sx={{ ml: 1 }} />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Financial Details</Typography>
                <Typography variant="body1"><strong>Amount:</strong> 
                  <Typography component="span" color={selectedEntry.type === 'income' ? 'success.main' : 'error.main'} fontWeight="bold" sx={{ ml: 1 }}>
                    ₦{selectedEntry.amount?.toLocaleString()}
                  </Typography>
                </Typography>
                <Typography variant="body1"><strong>Status:</strong> 
                  <Chip label={selectedEntry.payment_status || selectedEntry.status} size="small" color={getStatusColor(selectedEntry.payment_status || selectedEntry.status)} sx={{ ml: 1 }} />
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Source Information</Typography>
                <Typography variant="body1"><strong>Source:</strong> {getSourceLabel(selectedEntry.source)}</Typography>
                <Typography variant="body1"><strong>Category:</strong> {selectedEntry.category || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2">{selectedEntry.description}</Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialLedger;